<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edAttachment: { type: Object, default: null },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      attachmentFile: null,
      attachmentPreviewUrl: this.edAttachment?.url || '',
      removeAttachmentRequested: false,
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required: requiredIf(function attachmentIsMissing() {
        return !this.hasAttachment;
      }),
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    hasAttachment() {
      return (
        !!this.attachmentFile ||
        (!!this.edAttachment && !this.removeAttachmentRequested)
      );
    },
    attachmentName() {
      return this.attachmentFile?.name || this.edAttachment?.filename || '';
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.removeAttachment();
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          attachment: this.attachmentFile,
          remove_attachment: this.removeAttachmentRequested,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
    onAttachmentChange(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.revokeAttachmentPreview();
      this.attachmentFile = file;
      this.removeAttachmentRequested = false;
      this.attachmentPreviewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : '';
      this.v$.content.$touch();
    },
    removeAttachment() {
      this.revokeAttachmentPreview();
      this.attachmentFile = null;
      this.attachmentPreviewUrl = '';
      this.removeAttachmentRequested = !!this.edAttachment;
      this.v$.content.$touch();
    },
    revokeAttachmentPreview() {
      if (this.attachmentPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(this.attachmentPreviewUrl);
      }
    },
  },
  beforeUnmount() {
    this.revokeAttachmentPreview();
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>
        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ATTACHMENT.LABEL') }}
            <span class="text-n-slate-10 font-normal">
              {{ $t('CANNED_MGMT.ATTACHMENT.OPTIONAL') }}
            </span>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              @change="onAttachmentChange"
            />
          </label>
          <div
            v-if="hasAttachment"
            class="mt-2 flex items-center gap-3 rounded-lg border border-n-weak bg-n-slate-2 p-3"
          >
            <img
              v-if="attachmentPreviewUrl"
              :src="attachmentPreviewUrl"
              :alt="attachmentName"
              class="size-12 rounded object-cover"
            />
            <span v-else class="text-xl">📄</span>
            <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
              {{ attachmentName }}
            </span>
            <NextButton
              ghost
              slate
              xs
              icon="i-lucide-x"
              :label="$t('CANNED_MGMT.ATTACHMENT.REMOVE')"
              @click.prevent="removeAttachment"
            />
          </div>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ $t('CANNED_MGMT.ATTACHMENT.HELP') }}
          </p>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
